import { useEffect, useState, useCallback, useRef } from 'react';
import { useTimeWatcherContext } from '../TimeWatcherProvider';

/**
 * Custom hook for managing user inactivity timeout with countdown functionality.
 * Integrates with TimeWatcherContext to delegate timeout management and automatically
 * binds user activity event listeners to reset the timeout.
 *
 * @param {number} [defaultInactivityTimer=40] - Time in seconds before showing inactivity warning
 * @param {number} [defaultCountdownTimer=20] - Time in seconds for countdown before timeout
 * @param {Function} [onTimeout] - Callback function to execute when timeout occurs
 *
 * @returns {Object} Hook return object
 * @returns {Function} returns.resetTimeout - Function to reset the inactivity timer with current config
 */
export const useInactivityTimeout = ({
  inactivity: defaultInactivityTimer = 40,
  countdown: defaultCountdownTimer = 20,
  onTimeout,
  enabled = true,
}) => {
  const [showCountdown, setShowCountdown] = useState(false);
  const [inactivity, setInactivity] = useState(defaultInactivityTimer);
  const [countdown, setCountdown] = useState(defaultCountdownTimer);
  const inactivityRef = useRef(inactivity);
  const countdownRef = useRef(countdown);
  const timeoutRef = useRef(onTimeout);
  const enabledRef = useRef(enabled);

  const { addRunEachSecond, removeRunEachSecond, startTimer } =
    useTimeWatcherContext();

  const resetTimeout = useCallback(() => {
    setShowCountdown(false);
    setInactivity(defaultInactivityTimer);
    setCountdown(defaultCountdownTimer);
    inactivityRef.current = defaultInactivityTimer;
    countdownRef.current = defaultCountdownTimer;
  }, [defaultInactivityTimer, defaultCountdownTimer]);

  const handleTimeout = useCallback(() => {
    setInactivity((prev) => {
      if (prev > 0) {
        const next = prev - 1;
        inactivityRef.current = next;
        if (next === 0) {
          setShowCountdown(true);
        }
        return next;
      }
      return prev;
    });

    if (inactivityRef.current === 0) {
      setCountdown((prev) => {
        if (prev > 0) {
          const next = prev - 1;
          countdownRef.current = next;
          return next;
        }
        if (prev === 0 && onTimeout) {
          setShowCountdown(false);
          timeoutRef.current();
          removeRunEachSecond(handleTimeout);
        }
        return prev;
      });
    }
  }, []);

  useEffect(() => {
    timeoutRef.current = onTimeout;
    enabledRef.current = enabled;
  }, [onTimeout, enabled]);

  useEffect(() => {
    if (!enabled) {
      removeRunEachSecond(handleTimeout);
      return undefined;
    }

    startTimer();
    addRunEachSecond(handleTimeout);

    return () => removeRunEachSecond(handleTimeout);
  }, [
    enabled,
    startTimer,
    addRunEachSecond,
    removeRunEachSecond,
    handleTimeout,
  ]);

  const handleUserActivity = useCallback(() => {
    if (enabledRef.current) {
      resetTimeout();
    }
  }, [resetTimeout]);

  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [handleUserActivity]);

  return {
    inactivityTimer: inactivity,
    countdownTimer: countdown,
    showInactivityCountdown: showCountdown,
  };
};
